import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import User2FAPrompt from '../components/User2FAPrompt';
import LoginForm from '../components/auth/LoginForm';
import TwoFactorSetup from '../components/auth/TwoFactorSetup';
import TwoFactorVerify from '../components/auth/TwoFactorVerify';
import RecoveryCodesDisplay from '../components/auth/RecoveryCodesDisplay';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Mappa step → testi header (titolo + sottotitolo).
const STEP_HEADERS = {
  'login': {
    title: 'Accedi al gestionale',
    description: 'Inserisci le tue credenziali per accedere',
  },
  '2fa-setup': {
    title: 'Configura autenticazione a due fattori',
    description: "Per la sicurezza del tuo account, configura un'app authenticator",
  },
  '2fa-verify': {
    title: 'Verifica codice 2FA',
    description: "Inserisci il codice a 6 cifre dall'app authenticator",
  },
  'recovery-codes': {
    title: 'Codici di recupero',
    description: 'Salva questi codici in un luogo sicuro: ti serviranno se perdi il telefono',
  },
};

const LoginPage = () => {
  // Stato form principale
  const [step, setStep] = useState('login'); // 'login' | '2fa-setup' | '2fa-verify' | 'recovery-codes'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Stato 2FA
  const [pendingToken, setPendingToken] = useState('');
  const [code, setCode] = useState('');
  const [qrData, setQrData] = useState('');
  const [secret, setSecret] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // Prompt 2FA opzionale (dopo login utente senza 2FA configurato)
  const [show2faPrompt, setShow2faPrompt] = useState(false);
  // Il cookie httpOnly è già attivo: serve solo l'oggetto user da settare nel context
  const [pendingLoginUser, setPendingLoginUser] = useState(null);

  const { login, completeLoginWith2FA } = useAuth();
  const navigate = useNavigate();

  // === handler: login con email + password ===
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate('/');
    } else if (result.suggest_2fa) {
      setPendingLoginUser(result.pending_user);
      setShow2faPrompt(true);
    } else if (result.requires_2fa) {
      setPendingToken(result.pending_token);
      setError('');
      if (result.needs_setup) {
        try {
          const { data } = await axios.post(`${API_URL}/auth/2fa/setup-start`, {
            pending_token: result.pending_token,
          });
          setQrData(data.qr_data_url);
          setSecret(data.secret);
          setStep('2fa-setup');
        } catch (err) {
          setError(err.response?.data?.detail || 'Errore avvio 2FA');
        }
      } else {
        setStep('2fa-verify');
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // === handler: conferma setup 2FA (primo codice OTP) ===
  const handle2faSetupConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/2fa/setup-confirm`, {
        pending_token: pendingToken,
        code,
      });
      setRecoveryCodes(data.recovery_codes);
      // Il backend ha già impostato il cookie httpOnly: salviamo l'user per il post-recovery
      sessionStorage.setItem('_post2fa_user', JSON.stringify(data.user));
      setStep('recovery-codes');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  // === handler: verifica codice 2FA esistente ===
  const handle2faVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/2fa/verify`, {
        pending_token: pendingToken,
        code,
      });
      completeLoginWith2FA(null, data.user);
      if (data.recovery_used) {
        toast.warning(`Hai usato un codice di recupero. Codici rimanenti: ${data.recovery_remaining}`);
      }
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  // === handler: dopo aver salvato i recovery codes, completa il login ===
  const handleProceedAfterRecovery = () => {
    const userStr = sessionStorage.getItem('_post2fa_user');
    if (userStr) {
      completeLoginWith2FA(null, JSON.parse(userStr));
      sessionStorage.removeItem('_post2fa_user');
      navigate('/');
    }
  };

  // === handler: chiusura prompt 2FA opzionale → completa login standard ===
  const handle2faPromptClose = () => {
    setShow2faPrompt(false);
    if (pendingLoginUser) {
      completeLoginWith2FA(null, pendingLoginUser);
    }
    navigate('/');
  };

  // === handler: torna allo step di login (verify → login) ===
  const handleBackToLogin = () => {
    setStep('login');
    setCode('');
    setError('');
  };

  const header = STEP_HEADERS[step] || STEP_HEADERS.login;

  return (
    <div className="login-container">
      {/* Left side - Image */}
      <div
        className="login-image hidden lg:block"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/5472405/pexels-photo-5472405.jpeg?auto=compress&cs=tinysrgb&w=1200')`,
        }}
      >
        <div className="relative z-10 p-12 h-full flex flex-col justify-between">
          <div>
            <img src="/logo-white-cropped.svg" alt="SemplificAffitto" className="h-14 w-auto" data-testid="login-logo-white" />
          </div>
          <div className="text-white">
            <h1 className="text-4xl font-bold mb-4">Gestisci i tuoi affitti con semplicità</h1>
            <p className="text-white/80 text-lg">
              Immobili, contratti, pagamenti e documenti in un'unica piattaforma professionale.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="login-form-container">
        <div className="flex items-center justify-center mb-8">
          <img src="/logo-cropped.svg" alt="SemplificAffitto" className="h-14 w-auto" data-testid="login-logo-blue" />
        </div>

        <Card className="border-0 shadow-none">
          <CardHeader className="px-0">
            <CardTitle className="text-2xl font-bold text-slate-900">{header.title}</CardTitle>
            <CardDescription className="text-slate-500">{header.description}</CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            {step === 'login' && (
              <LoginForm
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                error={error}
                loading={loading}
                onSubmit={handleLoginSubmit}
              />
            )}

            {step === '2fa-setup' && (
              <TwoFactorSetup
                qrData={qrData}
                secret={secret}
                code={code}
                setCode={setCode}
                error={error}
                loading={loading}
                onSubmit={handle2faSetupConfirm}
              />
            )}

            {step === '2fa-verify' && (
              <TwoFactorVerify
                code={code}
                setCode={setCode}
                error={error}
                loading={loading}
                onSubmit={handle2faVerify}
                onBackToLogin={handleBackToLogin}
              />
            )}

            {step === 'recovery-codes' && (
              <RecoveryCodesDisplay
                codes={recoveryCodes}
                email={email}
                onProceed={handleProceedAfterRecovery}
              />
            )}
          </CardContent>
        </Card>

        <p className="mt-8 text-center text-sm text-slate-500">
          © 2026 SemplificApp.it — Gestione Affitti Professionale
        </p>
        <p className="mt-2 text-center text-xs text-slate-400">
          <a href="/privacy" className="hover:text-slate-600 underline" data-testid="login-privacy-link">
            Informativa Privacy
          </a>
        </p>
      </div>

      {/* 2FA Suggestion Prompt (visualizzato dopo login utente senza 2FA configurato) */}
      <User2FAPrompt
        open={show2faPrompt}
        onClose={handle2faPromptClose}
        userEmail={email}
      />
    </div>
  );
};

export default LoginPage;
